import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { backgroundColor, color, minWidth, size, space } from 'styled-system';

import AccessorySlider from '../../../accessory-slider/components/AccessorySlider';
import ArticlePreview from '../../components/ArticlePreview';
import Banner from '../../components/Banner';
import Icon from '../../components/Icon';
import { MessageType } from '../../enums/Alert';
import type { ItemPriceV3Fragment } from '../../gql/generated/schema';
import { useLocalization } from '../../providers/LocaleProvider';
import { fill } from '../../theme/system-utilities';
import type { AccessorySliderProps } from '../../types/accessory-slider';
import type { AlertMessage } from '../../types/alert';
import Separator from '../Separator';
import { BodyRegularBR2 } from '../Typography';
import CtaButtons from './CtaButtons';

interface CartAlertProps {
  message?: AlertMessage;
  article: Omit<ComponentProps<typeof ArticlePreview>, 'itemPrice_v3'>;
  accessorySliderProps?: AccessorySliderProps;
  itemPrice_v3: Pick<ItemPriceV3Fragment, 'original' | 'final'>;
}

const ICON_SIZE = 24;

const ICON_PROPS = {
  mr: 'sp_12',
  size: ICON_SIZE,
  minWidth: ICON_SIZE,
};

const BANNER_DEFAULT_PROPS = {
  py: ['18px', 'sp_16'],
  px: ['sp_16', null, 'sp_24', 'sp_32'],
};

const UnstyledSuccessIcon = styled(Icon)<any>`
  ${space};
  ${size};
  ${minWidth};
  ${fill}
`;

const SuccessIcon = (props: ComponentProps<typeof UnstyledSuccessIcon>) => (
  <UnstyledSuccessIcon
    {...ICON_PROPS}
    name={MessageType.Success}
    fill="sys.success.text.strong"
    {...props}
  />
);

const UnstyledWarningIcon = styled(SuccessIcon)<any>`
  ${fill}
`;

const WarningIcon = (props: ComponentProps<typeof UnstyledWarningIcon>) => (
  <UnstyledWarningIcon
    {...ICON_PROPS}
    name={MessageType.Warning}
    fill="sys.warning.background.hover"
    {...props}
  />
);

const UnstyledSuccessAlertBanner = styled(Banner)<any>`
  ${backgroundColor}
  ${color}
`;

const SuccessAlertBanner = (props: ComponentProps<typeof UnstyledSuccessAlertBanner>) => (
  <UnstyledSuccessAlertBanner
    {...BANNER_DEFAULT_PROPS}
    backgroundColor="sys.success.background.weak"
    color="sys.success.text.strong"
    type={MessageType.Success}
    {...props}
  />
);

const UnstyledWarningAlertBanner = styled(Banner)<any>`
  ${backgroundColor}
  ${color}
`;

const WarningAlertBanner = (props: ComponentProps<typeof UnstyledWarningAlertBanner>) => (
  <UnstyledWarningAlertBanner
    {...BANNER_DEFAULT_PROPS}
    backgroundColor="sys.warning.background.weak"
    color="sys.warning.background.hover"
    type={MessageType.Warning}
    {...props}
  />
);

const CartAlert = ({
  message: { colorType: type, translation } = {
    colorType: MessageType.Success,
    translation: 'h24_add_to_cart_success',
  },

  article,
  itemPrice_v3,
  accessorySliderProps,
}: CartAlertProps) => {
  const { t } = useLocalization();

  return (
    <>
      {type === MessageType.Success ? (
        <SuccessAlertBanner data-testid="cart-alert-banner" role="alert">
          <SuccessIcon alt={t('h24_success_icon')} />
          <BodyRegularBR2 as="span" color="inherit">
            {t(translation)}
          </BodyRegularBR2>
        </SuccessAlertBanner>
      ) : (
        <WarningAlertBanner data-testid="cart-alert-banner" role="alert">
          <WarningIcon alt={t('h24_warning_icon')} />
          <BodyRegularBR2 as="span" color="inherit">
            {t(translation)}
          </BodyRegularBR2>
        </WarningAlertBanner>
      )}
      <ArticlePreview itemPrice_v3={itemPrice_v3} {...article} />
      {accessorySliderProps && (
        <>
          <Separator />
          <AccessorySlider
            {...accessorySliderProps}
            disableOnSubmit
            withButtonAnimation
            disableDrawerOpening
            title="h24_accessory_slider_in_drawer_headline"
            ctaButtonKind="secondary"
          />
        </>
      )}
      <CtaButtons />
    </>
  );
};

export default CartAlert;
